/**
 * Redux slice (src/store/slices/studentsSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { ghostLogin, exitGhostModeAsync, activateGhostMode, exitGhostMode, clearGhostData, ghostTransportSettled } from "./ghostLoginSlice";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";
import {
  fetchAllstudents,
  setStudentArchiveStatus,
  Signup_Student,
  async_loaduser,
  async_removeuser,
  fetchStudentDetails,
  DeleteStudent,
  updateStudent,
  Signin_user,
  Signup_Student_By_Admin,
  FindUserByEmail,
  MatchOTP,
  Reset_Password,
  Signup_Student_With_Booking,
  SearchStudentbyUsername,
  listDriveFiles,
  downloadDriveFile,
  updatePhoneNumber,
  connectWebSocket,
  sendWebSocketMessage,
  disconnectWebSocket,
  updateStudentTimezone,
  initializeWebSocket,
  googleLogin,
  googleLoginReady,
  googleMfaRetired,
  setPasswordAfterOAuth,
} from "../actions/studentsActions";
import { fetchTeacherDetails } from "../actions/teachersActions";

let intialState = {
  studentslist: [],
  studentDetails: null,
  AllStudentlist: [],
  userType: null,
  loading: false,
  user: null,
  isAuthenticated: false,
  authInitialized: false,
  authLoading: false,
  authBootstrapRequestId: null,
  canonicalSession: null,
  canonicalBootstrapRequired: null,
  ghostTransitionRequestId: null,
  ghostRecovery: null,
  googleLoginRequestId: null,
  googleAttempt: null,
  googleCompletion: null,
  googleMfa: null,
  FoundedUser: [],
  Message: [],
  driveFiles: [],
  driveLoading: false,
  driveError: null,
  socket: null,
  isConnected: false,
  lastMessage: null,
  wsInitialized: false,
  wsError: null,
  timezoneUpdateLoading: false,
  timezoneUpdateError: null,
  currentArchiveScope: "active",
  archiveMutationLoadingById: {},
  archiveMutationError: null,
};

const upsertEntityById = (list = [], entity) => {
  if (!Array.isArray(list)) return entity ? [entity] : [];
  if (!entity?._id) return list;

  const index = list.findIndex((item) => item?._id === entity._id);
  if (index === -1) {
    return [...list, entity];
  }

  return [
    ...list.slice(0, index),
    { ...list[index], ...entity },
    ...list.slice(index + 1),
  ];
};

const removeEntityById = (list = [], entityId) =>
  Array.isArray(list) ? list.filter((item) => item?._id !== entityId) : [];

const mergeAuthenticatedTeacherDetails = (currentUser, teacherDetails) => {
  if (!currentUser || !teacherDetails || currentUser._id !== teacherDetails._id) {
    return currentUser;
  }

  return {
    ...currentUser,
    ...teacherDetails,
    UserType: currentUser.UserType || teacherDetails.UserType,
    Email: currentUser.Email || teacherDetails.Email,
    Username: currentUser.Username || teacherDetails.Username,
    roles: currentUser.roles || teacherDetails.roles,
    permissions: currentUser.permissions || teacherDetails.permissions,
  };
};

/**
 * RTK slice definition: studentsSlice.
 */
const studentsSlice = createSlice({
  name: "students",
  initialState: intialState,
  reducers: {
    /**
     * Reducer: loaduser.
     */
    loaduser: (state, action) => {
      state.canonicalSession = null;
      state.canonicalBootstrapRequired = null;
      state.authBootstrapRequestId = null;
      state.ghostTransitionRequestId = null;
      state.ghostRecovery = null;
      state.authLoading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
      state.userType = action.payload.UserType;
    },
    /**
     * Reducer: removeuser.
     */
    removeuser: (state, action) => {
      state.canonicalSession = null;
      state.canonicalBootstrapRequired = null;
      state.authBootstrapRequestId = null;
      state.ghostTransitionRequestId = null;
      state.ghostRecovery = null;
      state.authLoading = false;
      state.user = null;
      state.isAuthenticated = false;
      state.userType = null;
    },
    authInitializedNoSession: (state) => {
      state.canonicalSession = null;
      state.canonicalBootstrapRequired = null;
      state.authBootstrapRequestId = null;
      state.ghostTransitionRequestId = null;
      state.ghostRecovery = null;
      state.authLoading = false;
      state.authInitialized = true;
      state.isAuthenticated = false;
      state.authLoading = false;
    },
    /**
     * Reducer: websocketConnected.
     */
    websocketConnected: (state) => {
      state.isConnected = true;
    },
    /**
     * Reducer: websocketDisconnected.
     */
    websocketDisconnected: (state) => {
      state.isConnected = false;
      state.socket = null;
    },
    /**
     * Reducer: websocketMessageReceived.
     */
    websocketMessageReceived: (state, action) => {
      state.lastMessage = action.payload;
    },
  },

  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: fetchAllstudents.pending
      .addCase(fetchAllstudents.pending, (state, action) => {
        state.loading = true;
        state.currentArchiveScope = action.meta?.arg?.archiveScope || "active";
      })
      // extraReducers handler: fetchAllstudents.fulfilled
      .addCase(fetchAllstudents.fulfilled, (state, action) => {
        state.studentslist = action.payload;
        state.AllStudentlist = action.payload;
        state.loading = false;
        state.currentArchiveScope = action.meta?.arg?.archiveScope || "active";
      })
      // extraReducers handler: fetchAllstudents.rejected
      .addCase(fetchAllstudents.rejected, (state) => {
        state.loading = false;
      })
      .addCase(setStudentArchiveStatus.pending, (state, action) => {
        const studentId = action.meta?.arg?.studentId;
        if (studentId) {
          state.archiveMutationLoadingById[studentId] = true;
        }
        state.archiveMutationError = null;
      })
      .addCase(setStudentArchiveStatus.fulfilled, (state, action) => {
        const { studentId, isArchived, student } = action.payload || {};
        if (studentId) {
          delete state.archiveMutationLoadingById[studentId];
        }

        if (student?._id) {
          state.AllStudentlist = upsertEntityById(state.AllStudentlist, student);
        }

        if (state.currentArchiveScope === "all" && student?._id) {
          state.studentslist = upsertEntityById(state.studentslist, student);
          return;
        }

        const shouldRemoveFromVisibleList =
          (state.currentArchiveScope === "active" && isArchived) ||
          (state.currentArchiveScope === "archived" && !isArchived);

        if (shouldRemoveFromVisibleList) {
          state.studentslist = removeEntityById(state.studentslist, studentId);
          state.AllStudentlist = removeEntityById(state.AllStudentlist, studentId);
          return;
        }

        if (student?._id) {
          state.studentslist = upsertEntityById(state.studentslist, student);
        }
      })
      .addCase(setStudentArchiveStatus.rejected, (state, action) => {
        const studentId = action.payload?.studentId || action.meta?.arg?.studentId;
        if (studentId) {
          delete state.archiveMutationLoadingById[studentId];
        }
        state.archiveMutationError =
          action.payload?.message || action.error?.message || "Failed to update student archive status";
      })
      // extraReducers handler: Signup_Student.pending
      .addCase(Signup_Student.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Signup_Student.fulfilled
      .addCase(Signup_Student.fulfilled, (state, action) => {
        state.studentDetails = action.payload;
        state.AllStudentlist.push(action.payload);
        state.user = action.payload;
        state.userType = action.payload.UserType;
        state.isAuthenticated = true;
        state.loading = false;
        toast.success(t("store.students.signupSuccessful"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: Signup_Student.rejected
      .addCase(Signup_Student.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: Signup_Student_By_Admin.pending
      .addCase(Signup_Student_By_Admin.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Signup_Student_By_Admin.fulfilled
      .addCase(Signup_Student_By_Admin.fulfilled, (state, action) => {
        
        if (action.payload && action.payload.data) {
          state.AllStudentlist.push(action.payload.data.newStudent);
          state.loading = false;
        } else {
          toast.error(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: Signup_Student_By_Admin.rejected
      .addCase(Signup_Student_By_Admin.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: async_loaduser.pending
      .addCase(async_loaduser.pending, (state, action) => {
        state.authLoading = true;
        state.canonicalSession = null;
        state.canonicalBootstrapRequired = null;
        state.ghostRecovery = null;
        state.authBootstrapRequestId = action.meta.requestId;
      })
      // extraReducers handler: async_loaduser.fulfilled
      .addCase(async_loaduser.fulfilled, (state, action) => {
        if (state.authBootstrapRequestId !== action.meta.requestId) {
          return;
        }
        const expectedMfa = action.meta.arg;
        if (expectedMfa?.expectedMfaUserId !== undefined || expectedMfa?.expectedMfaRole !== undefined) {
          if (typeof expectedMfa.expectedMfaUserId !== 'string'
            || !/^[a-f0-9]{24}$/i.test(expectedMfa.expectedMfaUserId)
            || !['teacher', 'admin'].includes(expectedMfa.expectedMfaRole)
            || action.payload?._id !== expectedMfa.expectedMfaUserId
            || action.payload?.UserType !== expectedMfa.expectedMfaRole) {
            state.user = null;
            state.userType = null;
            state.isAuthenticated = false;
            state.authLoading = false;
            state.authInitialized = true;
            state.authBootstrapRequestId = null;
            return;
          }
        }
        state.canonicalSession = action.meta.canonicalSession || null;
        state.userType = action.payload.UserType;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.authLoading = false;
        state.authInitialized = true;
        state.authBootstrapRequestId = null;
      })
      // extraReducers handler: async_loaduser.rejected
      .addCase(async_loaduser.rejected, (state, action) => {
        if (state.authBootstrapRequestId !== action.meta.requestId) {
          return;
        }
        state.authLoading = false;
        state.canonicalSession = null;
        state.canonicalBootstrapRequired = null;
        state.authInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.userType = null;
        state.authBootstrapRequestId = null;
      })
      // extraReducers handler: async_removeuser.pending
      .addCase(async_removeuser.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: async_removeuser.fulfilled
      .addCase(async_removeuser.fulfilled, (state, action) => {
        // ✅ Clear ALL user-related state completely
        state.isAuthenticated = false;
        state.user = null;  // ✅ CRITICAL: Clear user object
        state.userType = null;
        state.loading = false;
        state.authInitialized = false;  // ✅ Reset auth initialization
        state.authLoading = false;

        // ✅ Also clear WebSocket state
        state.socket = null;
        state.isConnected = false;
        state.lastMessage = null;
        state.wsInitialized = false;
        state.wsError = null;
      })
      // extraReducers handler: async_removeuser.rejected
      .addCase(async_removeuser.rejected, (state) => {
        // No confirmed revocation: keep the current session and connections.
        state.loading = false;
      })
      // extraReducers handler: fetchStudentDetails.pending
      .addCase(fetchStudentDetails.pending, (state) => {
        state.loading = true;
        state.studentDetails = null;
      })
      // extraReducers handler: fetchStudentDetails.fulfilled
      .addCase(fetchStudentDetails.fulfilled, (state, action) => {
        state.studentDetails = action.payload;
        state.loading = false;
      })
      // extraReducers handler: fetchStudentDetails.rejected
      .addCase(fetchStudentDetails.rejected, (state) => {
        state.loading = false;
        state.studentDetails = null;
      })
      // extraReducers handler: DeleteStudent.fulfilled
      .addCase(DeleteStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.AllStudentlist = state.AllStudentlist.filter(
          (teacher) => teacher.id !== action.payload
        );
      })
      // extraReducers handler: DeleteStudent.rejected
      .addCase(DeleteStudent.rejected, (state, action) => {
        state.loading = true;
      })
      // extraReducers handler: updateStudent.pending
      .addCase(updateStudent.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: updateStudent.fulfilled
      .addCase(updateStudent.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update the user in state
        if (state.user && state.user._id === action.payload.student_ID) {
          state.user = {
            ...state.user,
            ...action.payload.updatedFields
          };
        }
        
        // Update in the AllStudentlist
        const studentIndex = state.AllStudentlist.findIndex(
          (student) => student._id === action.payload.student_ID
        );
        
        if (studentIndex !== -1) {
          state.AllStudentlist[studentIndex] = {
            ...state.AllStudentlist[studentIndex],
            ...action.payload.updatedFields
          };
        }
        
        toast.success(t("store.students.updatedSuccessfully"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: updateStudent.rejected
      .addCase(updateStudent.rejected, (state, action) => {
        state.loading = false;
        if (!action.meta?.arg?.suppressErrorToast) {
          toast.error(action.payload || t("store.students.updateFailed"));
        }
      })
      // extraReducers handler: Signin_user.fulfilled
      .addCase(Signin_user.pending, (state) => {
        state.loading = true;
        // Prevent a stale bootstrap `/me` response from overriding a fresh manual login.
        state.authBootstrapRequestId = null;
      })
      // extraReducers handler: Signin_user.fulfilled
      .addCase(Signin_user.fulfilled, (state, action) => {
        state.loading = false;
        // HTTP 200 can mean factor one only. Never hydrate an identity from a
        // challenge (including a malformed/conflicting challenge response),
        // and never retain an older signed-in identity while switching users.
        if (action.payload?.requires2FA !== undefined && action.payload.requires2FA !== false) {
          state.user = null;
          state.userType = null;
          state.isAuthenticated = false;
          state.authInitialized = true;
          state.authLoading = false;
          state.authBootstrapRequestId = null;
          return;
        }
        if (action.payload && action.payload.data) {
          state.userType = action.payload.data.UserType;
          state.user = action.payload.data;
          state.loading = false;
          state.isAuthenticated = true;
          state.authInitialized = true;
          state.authBootstrapRequestId = null;
        } else {
          toast.error(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: Signin_user.rejected
      .addCase(Signin_user.rejected, (state, action) => {
        state.loading = false;
        state.authInitialized = true;
        state.authBootstrapRequestId = null;
      })
      // extraReducers handler: FindUserByEmail.pending
      .addCase(FindUserByEmail.pending, (state, action) => {
        state.loading = true;
      })
      // extraReducers handler: FindUserByEmail.fulfilled
      .addCase(FindUserByEmail.fulfilled, (state, action) => {
        if (action.payload && action.payload.user) {
          state.FoundedUser = action.payload.user;
          state.loading = false;
        } else {
          toast.error(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: FindUserByEmail.rejected
      .addCase(FindUserByEmail.rejected, (state, action) => {
        state.loading = false;
      })
      // extraReducers handler: MatchOTP.pending
      .addCase(MatchOTP.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: MatchOTP.fulfilled
      .addCase(MatchOTP.fulfilled, (state, action) => {
        // remove thisaction.payload);
        if (action.payload && action.payload.message) {
          state.Message = action.payload.message;
          state.loading = false;
          toast.success(t("store.students.otpMatched"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        } else {
          toast.error(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: MatchOTP.rejected
      .addCase(MatchOTP.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: Reset_Password.pending
      .addCase(Reset_Password.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Reset_Password.fulfilled
      .addCase(Reset_Password.fulfilled, (state, action) => {
        const isSuccess = Boolean(action.payload?.success);
        state.loading = false;

        if (isSuccess) {
          // Keep existing authenticated user object intact.
          // Some reset responses include metadata only (e.g. { hasPassword, userId }).
          if (action.payload?.data?.UserType) {
            state.user = action.payload.data;
            state.userType = action.payload.data.UserType;
            state.isAuthenticated = true;
          }

          toast.success(t("store.students.passwordChanged"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        } else {
          toast.error(action.payload?.message || t("store.students.passwordResetFailed"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: Reset_Password.rejected
      .addCase(Reset_Password.rejected, (state, action) => {
        state.loading = false;
        if (action.payload || action.error?.message) {
          toast.error(action.payload || action.error?.message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: Signup_Student_With_Booking.pending
      .addCase(Signup_Student_With_Booking.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Signup_Student_With_Booking.fulfilled
      .addCase(Signup_Student_With_Booking.fulfilled, (state, action) => {
        // Handle both old and new response formats
        let studentData;
        
        // New format: action.payload.student or action.payload.data
        if (action.payload?.student) {
          studentData = action.payload.student;
        } else if (action.payload?.data?.UserType) {
          // New direct format
          studentData = action.payload.data;
        } else if (action.payload?.data?.data) {
          // Old nested format
          studentData = action.payload.data.data;
        }
        
        if (studentData) {
          state.studentDetails = studentData;
          state.AllStudentlist.push(studentData);
          state.user = studentData;
          state.userType = studentData.UserType;
          state.isAuthenticated = true;
          state.loading = false;
        } else {
          toast.error(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: Signup_Student_With_Booking.rejected
      .addCase(Signup_Student_With_Booking.rejected, (state) => {
        state.loading = false;
      })
      // SearchStudentbyUsername
      .addCase(SearchStudentbyUsername.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: SearchStudentbyUsername.fulfilled
      .addCase(SearchStudentbyUsername.fulfilled, (state, action) => {
        // remove thisaction.payload);
        if (action.payload && action.payload.data) {
          state.studentslist = action.payload.data.founded_Student;
          state.loading = false;
        } else {
          state.studentslist = action.payload.data;
          toast.warning(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: SearchStudentbyUsername.rejected
      .addCase(SearchStudentbyUsername.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: listDriveFiles.pending
      .addCase(listDriveFiles.pending, (state) => {
        state.driveLoading = true;
      })
      // extraReducers handler: listDriveFiles.fulfilled
      .addCase(listDriveFiles.fulfilled, (state, action) => {
        state.driveLoading = false;
        state.driveFiles = action.payload;
      })
      // extraReducers handler: listDriveFiles.rejected
      .addCase(listDriveFiles.rejected, (state, action) => {
        state.driveLoading = false;
        state.driveError = action.payload;
        toast.error(t("store.students.failedToListFiles"));
      })
      // extraReducers handler: downloadDriveFile.pending
      .addCase(downloadDriveFile.pending, (state) => {
        state.driveLoading = true;
      })
      // extraReducers handler: downloadDriveFile.fulfilled
      .addCase(downloadDriveFile.fulfilled, (state) => {
        state.driveLoading = false;
        toast.success(t("store.students.fileDownloaded"));
      })
      // extraReducers handler: downloadDriveFile.rejected
      .addCase(downloadDriveFile.rejected, (state, action) => {
        state.driveLoading = false;
        state.driveError = action.payload;
        toast.error(t("store.students.failedToDownloadFile"));
      })
      // extraReducers handler: updatePhoneNumber.pending
      .addCase(updatePhoneNumber.pending, (state) => {
        state.phoneUpdateLoading = true;
        state.phoneUpdateError = null;
      })
      // extraReducers handler: updatePhoneNumber.fulfilled
      .addCase(updatePhoneNumber.fulfilled, (state, action) => {
        state.phoneUpdateLoading = false;
        if (state.user) {
          state.user.Phone_Number = action.payload.Phone_Number;
        }
        toast.success(action.payload.message);
      })
      // extraReducers handler: updatePhoneNumber.rejected
      .addCase(updatePhoneNumber.rejected, (state, action) => {
        state.phoneUpdateLoading = false;
        state.phoneUpdateError = action.payload.message;
        toast.error(action.payload.message);
      })
      // extraReducers handler: connectWebSocket.fulfilled
      .addCase(connectWebSocket.fulfilled, (state, action) => {
        state.socket = action.payload;
      })
      // extraReducers handler: sendWebSocketMessage.rejected
      .addCase(sendWebSocketMessage.rejected, (state, action) => {
        console.error('Failed to send WebSocket message:', action.error.message);
      })
      // extraReducers handler: disconnectWebSocket.fulfilled
      .addCase(disconnectWebSocket.fulfilled, (state) => {
        state.socket = null;
        state.isConnected = false;
      })
      // extraReducers handler: updateStudentTimezone.pending
      .addCase(updateStudentTimezone.pending, (state) => {
        state.timezoneUpdateLoading = true;
        state.timezoneUpdateError = null;
      })
      // extraReducers handler: updateStudentTimezone.fulfilled
      .addCase(updateStudentTimezone.fulfilled, (state, action) => {
        state.timezoneUpdateLoading = false;
        if (state.user) {
          state.user.Timezone = action.payload.Timezone;
        }
        toast.success(t("store.students.timezoneUpdated"));
      })
      // extraReducers handler: updateStudentTimezone.rejected
      .addCase(updateStudentTimezone.rejected, (state, action) => {
        state.timezoneUpdateLoading = false;
        state.timezoneUpdateError = action.payload.message;
        toast.error(action.payload.message || t("store.students.failedToUpdateTimezone"));
      })
      // extraReducers handler: initializeWebSocket.pending
      .addCase(initializeWebSocket.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: initializeWebSocket.fulfilled
      .addCase(initializeWebSocket.fulfilled, (state) => {
        state.loading = false;
        state.wsInitialized = true;
      })
      // extraReducers handler: initializeWebSocket.rejected
      .addCase(initializeWebSocket.rejected, (state, action) => {
        state.loading = false;
        state.wsError = action.payload;
        toast.error(t("store.students.wsInitFailed"));
      })
      .addCase(googleLoginReady, (state, action) => {
        if (state.googleLoginRequestId !== action.payload?.requestId) return;
        state.googleAttempt = action.payload;
      })
      .addCase(googleMfaRetired, (state, action) => {
        if (state.googleMfa?.requestId !== action.payload?.requestId) return;
        state.googleMfa = null;
        state.googleCompletion = null;
      })
      // Only the current attempt can hand off MFA or finish a canonical bootstrap.
      .addCase(googleLogin.pending, (state, action) => {
        state.loading = true;
        state.googleLoginRequestId = action.meta.requestId;
        state.googleAttempt = null;
        state.googleCompletion = null;
        state.googleMfa = null;
        state.authBootstrapRequestId = null;
        state.user = null;
        state.userType = null;
        state.isAuthenticated = false;
        state.authInitialized = true;
        state.canonicalSession = null;
        state.canonicalBootstrapRequired = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        if (state.googleLoginRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.googleLoginRequestId = null;
        state.googleAttempt = null;
        if (action.payload?.requires2FA === true) {
          state.googleMfa = { ...action.payload, requestId: action.meta.requestId,
            expiresAt: Date.now() + Math.min(Number(action.payload.expiresInSeconds) || 0, 300) * 1000 };
          state.googleCompletion = { requestId: action.meta.requestId, kind: 'mfa' };
          return;
        }
        // async_loaduser already owns identity and its canonical session. Never
        // rehydrate from Google JSON or clear the completed bootstrap here.
        if (state.isAuthenticated && state.canonicalSession?.mode === 'direct'
          && state.canonicalSession.userId === action.payload?.data?._id
          && state.canonicalSession.userType === action.payload?.data?.UserType) {
          state.googleCompletion = { requestId: action.meta.requestId, kind: 'session' };
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        if (state.googleLoginRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.googleLoginRequestId = null;
        state.googleAttempt = null;
        state.googleCompletion = null;
        state.googleMfa = null;
        state.authBootstrapRequestId = null;
      })
      // extraReducers handler: setPasswordAfterOAuth.pending
      .addCase(setPasswordAfterOAuth.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: setPasswordAfterOAuth.fulfilled
      .addCase(setPasswordAfterOAuth.fulfilled, (state, action) => {
        state.loading = false;
        // Update user to indicate they now have a password
        if (state.user) {
          state.user.Password = "***"; // Don't store actual password, just indicate it exists
        }
        toast.success(t("store.students.passwordSetSuccessfully"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: setPasswordAfterOAuth.rejected
      .addCase(setPasswordAfterOAuth.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.message || t("store.students.failedToSetPassword"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // Handle fetchTeacherDetails to update the authenticated user with fresh teacher data.
      .addCase(fetchTeacherDetails.fulfilled, (state, action) => {
        // Only update if the fetched teacher is the current authenticated user.
        // Public teacher detail responses can be narrower than `/me`; never let
        // them strip auth-critical fields such as UserType from the session.
        if (state.user && state.user._id === action.payload._id) {
          state.user = mergeAuthenticatedTeacherDetails(state.user, action.payload);
        }
      })
      .addCase(ghostTransportSettled, (state, action) => {
        const recovery = state.ghostRecovery;
        if (!recovery || recovery.requestId !== action.payload?.requestId) return;
        recovery.transportSettled = true;
        if (recovery.needsRecovery && state.isAuthenticated && !state.authLoading
          && !state.ghostTransitionRequestId) {
          state.canonicalBootstrapRequired = { requestId: recovery.requestId, revalidate: true };
        }
      })
      .addMatcher(isAnyOf(ghostLogin.pending, exitGhostModeAsync.pending), (state, action) => {
        // Every new request is unresolved until its own ACK and fresh bootstrap.
        state.ghostTransitionRequestId = action.meta.requestId;
        state.ghostRecovery = { requestId: action.meta.requestId, transportSettled: false, needsRecovery: false };
        state.canonicalSession = null;
        state.canonicalBootstrapRequired = null;
        state.authBootstrapRequestId = null;
        state.authLoading = false;
      })
      .addMatcher(isAnyOf(
        ghostLogin.fulfilled, ghostLogin.rejected,
        exitGhostModeAsync.fulfilled, exitGhostModeAsync.rejected,
      ), (state, action) => {
        // The ghost reducer also ignores retired completions. Do not let one
        // revoke a successor's canonical proof or in-flight /me ownership here.
        if (state.ghostTransitionRequestId !== action.meta.requestId) return;
        state.ghostTransitionRequestId = null;
        state.canonicalSession = null;
        state.canonicalBootstrapRequired = null;
        state.authBootstrapRequestId = null;
        state.authLoading = false;
        if (ghostLogin.rejected.match(action) || exitGhostModeAsync.rejected.match(action)) {
          if (state.ghostRecovery?.requestId === action.meta.requestId) {
            state.ghostRecovery.needsRecovery = true;
            if (state.ghostRecovery.transportSettled && state.isAuthenticated) {
              state.canonicalBootstrapRequired = { requestId: action.meta.requestId, revalidate: true };
            }
          }
        } else {
          state.ghostRecovery = null;
        }
      })
      .addMatcher(isAnyOf(
        activateGhostMode, exitGhostMode, clearGhostData,
        Signin_user.pending, Signin_user.fulfilled, Signin_user.rejected,
        Signup_Student.pending, Signup_Student.fulfilled, async_removeuser.fulfilled,
      ), (state) => {
        // Synchronous cleanup/auth replacement retires any ghost request.
        state.ghostTransitionRequestId = null;
        state.ghostRecovery = null;
        state.canonicalSession = null;
        state.canonicalBootstrapRequired = null;
        state.authBootstrapRequestId = null;
        state.authLoading = false;
      })
      .addMatcher(isAnyOf(Signin_user.fulfilled, Signup_Student.fulfilled), (state, action) => {
        const signup = action.type === Signup_Student.fulfilled.type;
        const returnedUser = signup ? action.payload : action.payload?.data;
        const confirmedResponse = signup || (action.payload?.success === true
          && action.payload?.token === true
          && (action.payload?.requires2FA === undefined || action.payload.requires2FA === false));
        // This is a request to verify the cookie, never an admission credential.
        state.canonicalBootstrapRequired = confirmedResponse
          && typeof returnedUser?._id === 'string' && /^[a-f0-9]{24}$/i.test(returnedUser._id)
          && (signup ? returnedUser.UserType === 'student'
            : ['student', 'teacher', 'admin', 'accountant'].includes(returnedUser.UserType))
          ? { id: returnedUser._id, role: returnedUser.UserType, mode: 'direct', requestId: action.meta.requestId } : null;
      })
      .addMatcher((action) => isAnyOf(
        Signin_user.pending, Signin_user.fulfilled, Signin_user.rejected,
        Signup_Student.pending, async_removeuser.pending, async_removeuser.fulfilled,
        activateGhostMode, exitGhostMode, clearGhostData, ghostLogin.pending, exitGhostModeAsync.pending,
        async_loaduser.pending,
      )(action) || ['students/loaduser', 'students/removeuser', 'students/authInitializedNoSession'].includes(action.type),
      (state, action) => {
        const ownCleanup = clearGhostData.match(action)
          && state.googleLoginRequestId && action.payload?.googleRequestId === state.googleLoginRequestId;
        const ownBootstrap = async_loaduser.pending.match(action)
          && ((state.googleLoginRequestId && action.meta.arg?.expectedSession?.requestId === state.googleLoginRequestId)
            || (state.googleMfa && action.meta.arg?.expectedMfaUserId === state.googleMfa.userId
              && action.meta.arg?.expectedMfaRole === state.googleMfa.userType));
        if (ownCleanup || ownBootstrap) return;
        state.googleLoginRequestId = null;
        state.googleAttempt = null;
        state.googleCompletion = null;
        state.googleMfa = null;
      });
  },
});

export const { loaduser, removeuser,  websocketConnected, websocketDisconnected, websocketMessageReceived } = studentsSlice.actions;
export default studentsSlice.reducer;
